import { useQueryClient } from '@tanstack/react-query';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { IconButton } from '@/components/ui/IconButton';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { RemoveControl } from '@/features/integration-management/accounts-tab';
import { Card } from '@/features/integrations/add-integrations-modal';
import type { ReconnectAccount } from '@/features/integrations/add-integrations.hooks';
import { integrationKeys } from '@/features/integrations/integrations.query';
import { mcpKeys } from '@/features/mcp/mcp.query';
import { IntegrationIcon } from '@/integrations/integration-icon';
import { connectIntegration } from '@/integrations/integrations.utils';

const RECONNECT_SOURCE = 'integrations_page:reconnect';

const ReconnectAccountCard = ({ account }: { account: ReconnectAccount }) => {
  const queryClient = useQueryClient();
  const reconnect = () =>
    void connectIntegration(account.target, RECONNECT_SOURCE);
  const remove = async () => {
    if (account.target.kind === 'integration') {
      await backendApis.integrations.disconnectIntegration(account.id);
    } else {
      await backendApis.mcp.deleteServer(account.id);
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: integrationKeys.all }),
      queryClient.invalidateQueries({ queryKey: mcpKeys.all }),
    ]);
  };
  return (
    <Card
      icon={
        <IntegrationIcon
          iconKey={account.iconKey}
          iconUrl={account.iconUrl}
          name={account.displayName}
          className="size-full"
        />
      }
      name={
        <>
          {account.displayName}
          {account.accountLabel && (
            <span className="font-normal text-foreground-muted">
              {' · '}
              {account.accountLabel}
            </span>
          )}
        </>
      }
      description="Needs your authorization"
      action={
        <XStack className="shrink-0 items-center gap-1">
          <span
            onClick={(event) => event.stopPropagation()}
            className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
          >
            <RemoveControl name={account.displayName} onRemove={remove} />
          </span>
          <IconButton
            icon={Icons.refresh}
            variant="ghost"
            size="lg"
            onClick={(event) => {
              event.stopPropagation();
              reconnect();
            }}
            className="relative isolate shrink-0 hover:bg-transparent before:pointer-events-none before:absolute before:inset-1.5 before:-z-10 before:rounded-md before:content-[''] hover:before:bg-surface-active"
            tooltip="Reconnect"
            aria-label={`Reconnect ${account.displayName}`}
          />
        </XStack>
      }
      onClick={reconnect}
    />
  );
};

export const ReconnectAccounts = ({
  accounts,
}: {
  accounts: ReconnectAccount[];
}) => (
  <YStack className="gap-4">
    <h3 className="text-sm font-medium text-foreground-muted">
      Needs re-connection
    </h3>
    {/* -mx-2 cancels the cards' p-2, so their icons line up with the label */}
    <Grid className="-mx-2 grid-cols-2 gap-5">
      {accounts.map((account) => (
        <ReconnectAccountCard key={account.id} account={account} />
      ))}
    </Grid>
  </YStack>
);
